import React, { useMemo } from 'react';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChart, {
  SplineChartOptionsOverride,
  SplineChartProps
} from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import { useAppSelector } from 'src/utils/Hooks';
import SplineChartLoading, {
  SplineChartLoadingProps
} from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { getSearchAppName, getQueryParamValue } from 'src/utils/app';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { useMetricByWeekId, UseMetricByWeekIdArgs } from 'src/serverProxy/useMetricByWeekId';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertLineChartSeriesToDelimitedData, {
  Series
} from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import saveAs from 'file-saver';

export interface BeaconMetricSplineChartProps
  extends Pick<
      BeaconChartWithLegendProps,
      'singleDropdownProps' | 'buildDropdownMenuOptions' | 'dropdownOptionHandler'
    >,
    UseMetricByWeekIdArgs {
  indexName: string;
  fieldName: string;
  title?: string;
  /**
   * Hides the title and category subtitle if false
   */
  showTitleSummary?: boolean;
  /**
   * Optional overrides to the default Highcharts chart options
   */
  chartOptionsOverride?: SplineChartOptionsOverride;
  /**
   * Used for indexing overrides for the index field
   */
  entityType?: string;
  splineChartLoadingProps?: SplineChartLoadingProps;
  beaconChartWithLegendOverride?: (
    props: Omit<BeaconChartWithLegendProps, 'children'>,
    metricByValues: ReturnType<typeof useMetricByWeekId>
  ) => Omit<BeaconChartWithLegendProps, 'children'>;
  splineChartProps?: Partial<SplineChartProps>;
  byDayId?: boolean;
}

/**
 * Given an index and a field name, displays a weekly spline chart
 */
const BeaconMetricSplineChart = ({
  fieldName,
  indexName,
  title,
  showTitleSummary = true,
  chartOptionsOverride,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  splineChartLoadingProps,
  metricTypeDisplay,
  aggregationFunctionForTotal,
  requestBuilder,
  comparisonFieldName,
  comparisonIndexName,
  isComparisonDataCustom,
  buildDropdownMenuOptions,
  dropdownOptionHandler,
  beaconChartWithLegendOverride,
  splineChartProps,
  byDayId,
  customFilterComparisonForTotal,
  ...chartWithLegendProps
}: BeaconMetricSplineChartProps) => {
  const metricByValues = useMetricByWeekId({
    fieldName,
    indexName,
    entityType,
    primaryAggregationBuilder,
    secondaryAggregationBuilder,
    metricTypeDisplay,
    aggregationFunctionForTotal,
    requestBuilder,
    comparisonFieldName,
    comparisonIndexName,
    isComparisonDataCustom,
    customFilterComparisonForTotal,
    byDayId
  });
  const {
    primaryData,
    secondaryData,
    isLoading,
    formattedPrimaryTotal,
    formattedSecondaryTotal,
    formattedPercentChange,
    percentChange,
    formattedLastPrimaryValue,
    formattedLastSecondaryValue,
    percentChangeLastValues,
    formattedPercentChangeLastValues
  } = metricByValues;
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);

  const field = useMemo(
    () => INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType),
    [indexName, fieldName, entityType]
  );

  const useLatestWeek = field && field.timePeriodAggregationFunctionType === 'lastValue';
  const mainMetric = useLatestWeek ? formattedLastPrimaryValue : formattedPrimaryTotal;
  const secondaryMetric = useLatestWeek ? formattedLastSecondaryValue : formattedSecondaryTotal;

  const rawChange = useLatestWeek ? percentChangeLastValues : percentChange;
  const change = useLatestWeek ? formattedPercentChangeLastValues : formattedPercentChange;

  const titleToUse = title || field.displayName;
  const useDailyData = getQueryParamValue('tab') === 'advertising';

  /**
   * In case of prior-period, the primary data is added with the last value of the secondary data
   * This happens inside useMetricByWeekId when calculating the final primary data
   * This primary data is as expected for the chart to fluently connect prior line with current line
   * But for exporting to CSV (in case of using dayId in Advertising),
   * we do not need the last value of the secondary data in the primary data
   */
  const convertSeriesToDelimitedData = () => {
    try {
      // If the comparison time period is prior period and the data is daily, remove the first row of the primary data
      const csvExportedPrimaryData =
        comparisonTimePeriod.id === 'prior-period' && useDailyData && primaryData.length > 1
          ? primaryData.slice(1, primaryData.length)
          : primaryData;

      // Convert the chart series to delimited data (may return a promise)
      const data = convertLineChartSeriesToDelimitedData(
        [
          {
            type: 'spline',
            name: comparisonTimePeriod.displayName,
            data: secondaryData.map((row) => ({
              x: row[0],
              y: row[1],
              marker: {
                enabled: true
              }
            }))
          } as unknown as Series,
          {
            type: 'spline',
            name: mainTimePeriod.displayName,
            data: csvExportedPrimaryData.map((row) => ({
              x: row[0],
              y: row[1],
              marker: {
                enabled: true
              }
            }))
          } as unknown as Series
        ],
        comparisonTimePeriod.id === 'prior-period'
      );

      // Create a blob with the data and save it as a CSV file
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `${titleToUse}.csv`);
    } catch (e) {
      console.error('error: ', e);
    }

    return Promise.resolve();
  };

  const getBeaconChartWithLegendProps = () => {
    const defaultProps: Omit<BeaconChartWithLegendProps, 'children'> = {
      title: titleToUse,
      primaryLegendProps: {
        displayName: mainTimePeriod.displayName,
        metric: mainMetric,
        change,
        direction: getDirection(rawChange)
      },
      comparisonLegendProps: {
        displayName: comparisonTimePeriod.displayName,
        metric: secondaryMetric,
        direction: getDirection(rawChange)
      },
      loading: isLoading,
      loadingComponent: <SplineChartLoading {...splineChartLoadingProps} />,
      showTitleSummary,
      buildDropdownMenuOptions,
      dropdownOptionHandler,
      convertSeriesToDelimitedData,
      ...chartWithLegendProps
    };

    if (beaconChartWithLegendOverride) {
      return beaconChartWithLegendOverride(defaultProps, metricByValues);
    }

    return defaultProps;
  };
  return (
    <BeaconChartWithLegend {...getBeaconChartWithLegendProps()}>
      <SplineChart
        byDayId={byDayId}
        primaryData={primaryData}
        secondaryData={secondaryData}
        metricType={metricTypeDisplay || field.metricType}
        chartOptionsOverride={chartOptionsOverride}
        {...splineChartProps}
      />
    </BeaconChartWithLegend>
  );
};

export default BeaconMetricSplineChart;
